import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
  MAIL_FILTER_FIELDS,
  MAIL_FILTER_OPERATORS,
  MailFilter,
  MailFilterOperator,
  MailFolderDto,
  MailSearchHitDto,
  MailSearchRequest,
  MailService,
} from './mail.service';
import { MailDetailComponent } from './mail-detail/mail-detail.component';

const FILTER_STATE_STORAGE_KEY = 'mail-filter-state';

interface MailFilterState {
  filters: MailFilter[];
  freeText: string;
  sortField: string;
  sortOrder: 'asc' | 'desc';
}

@Component({
  selector: 'app-mail',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './mail.component.html',
  styleUrl: './mail.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MailComponent implements OnInit {
  readonly fieldSuggestions = MAIL_FILTER_FIELDS;
  readonly operators = MAIL_FILTER_OPERATORS;

  folders: MailFolderDto[] = [];
  filters: MailFilter[] = [];
  freeText = '';
  page = 0;
  size = 25;
  sortField = '@timestamp';
  sortOrder: 'asc' | 'desc' = 'desc';

  hits: MailSearchHitDto[] = [];
  total = 0;
  loading = false;

  constructor(
    private mailService: MailService,
    private toastr: ToastrService,
    private modalService: NgbModal,
  ) {}

  ngOnInit(): void {
    this.mailService.getFolders().subscribe({
      next: folders => this.folders = folders,
      error: (err) => this.toastr.error('Failed to load folders: ' + err.message),
    });
    this.loadFilterState();
    this.search();
  }

  addFilter(): void {
    this.filters = [...this.filters, { field: 'folder', operator: 'EQUALS', value: '' }];
  }

  removeFilter(index: number): void {
    this.filters = this.filters.filter((_, i) => i !== index);
  }

  needsValue(operator: MailFilterOperator): boolean {
    return operator !== 'EXISTS' && operator !== 'NOT_EXISTS' && operator !== 'IN';
  }

  needsValues(operator: MailFilterOperator): boolean {
    return operator === 'IN';
  }

  setValuesFromCsv(filter: MailFilter, csv: string): void {
    filter.values = csv.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
  }

  getFromField(hit: MailSearchHitDto): { field: string; value: string } | null {
    if (hit.fromName) {
      return { field: 'fromName', value: hit.fromName };
    }
    if (hit.fromEmail) {
      return { field: 'fromEmail', value: hit.fromEmail };
    }
    if (hit.from) {
      return { field: 'from', value: hit.from };
    }
    return null;
  }

  addQuickFilter(field: string, value: string | null | undefined, exclude: boolean): void {
    if (!field || !value) {
      return;
    }
    const operator: MailFilterOperator = exclude ? 'NOT_EQUALS' : 'EQUALS';
    const alreadyPresent = this.filters.some(
      (f) => f.field === field && f.operator === operator && f.value === value,
    );
    if (!alreadyPresent) {
      this.filters = [...this.filters, { field, operator, value }];
    }
    this.search();
  }

  search(): void {
    this.page = 0;
    this.saveFilterState();
    this.runSearch();
  }

  reset(): void {
    this.filters = [];
    this.freeText = '';
    this.sortField = '@timestamp';
    this.sortOrder = 'desc';
    localStorage.removeItem(FILTER_STATE_STORAGE_KEY);
    this.search();
  }

  private saveFilterState(): void {
    const state: MailFilterState = {
      filters: this.filters,
      freeText: this.freeText,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
    };
    localStorage.setItem(FILTER_STATE_STORAGE_KEY, JSON.stringify(state));
  }

  private loadFilterState(): void {
    const saved = localStorage.getItem(FILTER_STATE_STORAGE_KEY);
    if (!saved) {
      return;
    }
    try {
      const state: MailFilterState = JSON.parse(saved);
      this.filters = state.filters ?? [];
      this.freeText = state.freeText ?? '';
      this.sortField = state.sortField ?? this.sortField;
      this.sortOrder = state.sortOrder ?? this.sortOrder;
    } catch {
      localStorage.removeItem(FILTER_STATE_STORAGE_KEY);
    }
  }

  nextPage(): void {
    if ((this.page + 1) * this.size < this.total) {
      this.page++;
      this.runSearch();
    }
  }

  prevPage(): void {
    if (this.page > 0) {
      this.page--;
      this.runSearch();
    }
  }

  private runSearch(): void {
    const request: MailSearchRequest = {
      filters: this.filters,
      freeText: this.freeText || null,
      page: this.page,
      size: this.size,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
    };

    this.loading = true;
    this.mailService.search(request).subscribe({
      next: (res) => {
        this.hits = res.hits;
        this.total = res.total;
        this.loading = false;
      },
      error: (err) => {
        this.toastr.error('Search failed: ' + err.message);
        this.loading = false;
      },
    });
  }

  showDetail(hit: MailSearchHitDto): void {
    this.mailService.getDetail(hit.messageId, hit.folder).subscribe({
      next: (detail) => {
        const ref = this.modalService.open(MailDetailComponent, { size: 'xl' });
        ref.componentInstance.detail = detail;
      },
      error: (err) => this.toastr.error('Failed to load mail detail: ' + err.message),
    });
  }
}
